import { useEffect, useCallback } from 'react';
import { doc, setDoc, updateDoc, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { pushTokenDoc, notificationQueueColl, dailyAnswerDoc } from '../db';
import { UserProfile } from '../App';

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || '';

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Write a push notification request to Firestore.
 * Cloud Function (couples/{coupleId}/notificationQueue) picks it up and sends FCM push.
 */
export async function queueNotification(
  coupleId: string,
  toUid: string,
  title: string,
  body: string,
  tag = 'default',
) {
  if (!toUid) return;
  try {
    await addDoc(notificationQueueColl(coupleId), {
      toUid,
      title,
      body,
      tag,
      createdAt: serverTimestamp(),
    });
  } catch {
    // Push notifications are optional — silent fail
  }
}

export function usePushNotification(user: UserProfile | null) {
  // Register service worker + get FCM token + save to Firestore
  const init = useCallback(async () => {
    if (!user) return;
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
    if (Notification.permission === 'denied') return;

    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return;
    }

    try {
      // Register our custom SW (replaces firebase-messaging-sw.js)
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

      if (!VAPID_KEY) return; // VAPID key required for FCM token

      const { getMessaging, getToken } = await import('firebase/messaging');
      const { default: app } = await import('../firebase');
      const messaging = getMessaging(app);

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: reg,
      });

      if (!token) return;

      // Persist token in two places for lookup flexibility
      await updateDoc(doc(db, 'users', user.uid), { fcmToken: token });
      await setDoc(pushTokenDoc(user.coupleId, user.uid), {
        fcmToken: token,
        uid: user.uid,
        displayName: user.displayName,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn('[Push] Setup failed:', err);
    }
  }, [user]);

  // Daily rhythm reminder: show local notification at 22:00 if user hasn't answered today.
  useEffect(() => {
    if (!user?.coupleId) return;

    const now = new Date();
    const target = new Date();
    target.setHours(22, 0, 0, 0);
    if (target <= now) return; // already past 22:00 today

    const delay = target.getTime() - now.getTime();
    const timer = window.setTimeout(async () => {
      if (Notification.permission !== 'granted') return;
      try {
        const snap = await getDoc(dailyAnswerDoc(user.coupleId, getTodayKey()));
        const answered = snap.exists() && !!snap.data()[user.uid];
        if (answered) return;

        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        reg?.showNotification('Bugünün ritmi açık kaldı', {
          body: 'Bir cevap bırakırsan bugün de ikinizden bir iz kalacak.',
          icon: '/logo192.png',
          badge: '/favicon.png',
          tag: 'streak-danger',
          vibrate: [300, 150, 300],
        } as NotificationOptions);
      } catch {
        // Silent
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [user?.coupleId, user?.uid]);

  return { init };
}
